import { useState } from "react";
import { SubmitHandler, useFormContext, useWatch } from "react-hook-form";
import { Image, Pressable, StyleSheet } from "react-native";

import {
  BMButton,
  BMInputField,
  BMPrimitives,
  BMText,
} from "@traveloka/district-bloom";
import IcSystemEyeHide from "@traveloka/icon-kit-web/react/IcSystemEyeHide";
import IcSystemEyeShow from "@traveloka/icon-kit-web/react/IcSystemEyeShow";

import BMView from "../../../../components/View/BMView";

import { LOGO_BASE64 } from "../../../../assets/logo";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { appendTestId } from "../../../../utils";
import { isTravelokaEmailDetected } from "../helper";
import { LoginFormValues } from "./type";

type Props = {
  isLoading?: boolean;
  onSubmit?: SubmitHandler<LoginFormValues>;
  onClickLogo?: () => void;
  onClickForgotPassword?: () => void;
};

const testID = "login";

export default function Login({
  isLoading = false,
  onSubmit,
  onClickLogo,
  onClickForgotPassword,
}: Props) {
  const [hidePasswordChar, setHidePasswordChar] = useState(true);
  const [hidePasswordField, setHidePasswordField] = useState(false);

  const {
    setValue,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState: { isValid, errors },
  } = useFormContext<LoginFormValues>();
  const [email, password] = useWatch({ name: ["email", "password"] });

  const { CorporateLoginPage: cr } = useMobileContentResource();

  return (
    <BMView align="center" spacing="xl">
      <BMView align="center" spacing="xl4">
        <Pressable
          onPress={onClickLogo}
          testID={appendTestId(testID, "logo_button")}
        >
          <Image style={styles.logo} source={{ uri: LOGO_BASE64 }} />
        </Pressable>
        <BMText
          variant="titleNormal"
          ink="BMStyleTextInkPrimary"
          testID={appendTestId(testID, "title")}
        >
          {cr.pageTitle}
        </BMText>
      </BMView>
      <BMView style={styles.form} spacing="lg">
        <BMInputField
          value={email}
          label={cr.emailLabel}
          placeholder={cr.emailPlaceholder}
          disabled={isLoading}
          onChange={(e) => {
            const value = e.nativeEvent.text;
            setHidePasswordField(isTravelokaEmailDetected(value));
            setValue("email", value, { shouldValidate: true });
            clearErrors("root");
          }}
          onSubmitEditing={() => {
            rhfHandleSubmit(onSubmit ?? (() => {}))();
          }}
          testID={appendTestId(testID, "email_input")}
        />
        {!hidePasswordField ? (
          <BMInputField
            value={password}
            label={cr.passwordLabel}
            buttonInsetType="BUTTON_ICON"
            secureTextEntry={hidePasswordChar}
            placeholder={cr.passwordPlaceholder}
            disabled={isLoading}
            buttonInsetProps={{
              Icon: hidePasswordChar ? IcSystemEyeHide : IcSystemEyeShow,
              onPress: () => setHidePasswordChar((prev) => !prev),
            }}
            onChange={(e) => {
              setValue("password", e.nativeEvent.text, {
                shouldValidate: true,
              });
              clearErrors("root");
            }}
            onSubmitEditing={() => {
              rhfHandleSubmit(onSubmit ?? (() => {}))();
            }}
            testID={appendTestId(testID, "password_input")}
          />
        ) : null}
        <BMView align="center" spacing="lg">
          {errors.root?.message && (
            <BMText variant="uiSmall" ink="BMStyleIconInkHighlightRed">
              {errors.root.message}
            </BMText>
          )}
          <Pressable
            onPress={onClickForgotPassword}
            testID={appendTestId(testID, "forgot_password")}
          >
            <BMText variant="uiLarge" ink="BMStyleTextInkAction">
              {cr.forgotPasswordCTA}
            </BMText>
          </Pressable>
        </BMView>
        <BMButton
          label={cr.submitButton}
          role="PRIMARY"
          size="LARGE"
          stretchable
          loading={isLoading}
          disabled={!!errors.root || isLoading || !isValid}
          onPress={rhfHandleSubmit(onSubmit ?? (() => {}))}
          testID={appendTestId(testID, "submit_button")}
        />
      </BMView>
    </BMView>
  );
}

const styles = StyleSheet.create({
  logo: {
    height: 28,
    width: 280,
    resizeMode: "contain",
  },
  form: {
    width: "100%",
    paddingHorizontal: BMPrimitives.BMSize.lg,
  },
});
